import { inject } from "@angular/core";
import { catchError, exhaustMap, map, of, switchMap, tap } from "rxjs";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { authActions } from "./auth.actions";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { settings } from "src/app/settings/settings";
import { Router } from "@angular/router";
import {AuthenticationService} from "../../auth/services/authentication.service";
import {Store} from "@ngrx/store";
import {EventsWebsocketService} from "../../services/entities/events-websocket.service";
import {entityActions} from "../entities/entity.actions";
import {CollisionType, Components, EntityType} from "../entities/entity.model";
import {Vector3} from "three";
import {LookingAtProperties} from "../entities/component-properties-dto/looking-at.properties.dto";
import {cameraActions} from "../camera/camera.actions";
import {worldActions} from "../world/world.actions";
import {PushNotificationService} from "../../services/notifications/push-notification-service.service";
import {networkActions} from "../network/network.actions";

export const login = createEffect(
  (actions$ = inject(Actions), httpClient = inject(HttpClient)) => {
    return actions$.pipe(
      ofType(authActions.loginStarted),
      switchMap((action) => {
        const body = new HttpParams().appendAll({
          grant_type: "password",
          username: action.username,
          password: action.password,
        });
        const headers = new HttpHeaders({
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: "Basic " + btoa("binst-site" + ":" + "secret"),
        });
        return httpClient
          .post(`${settings.apiUrl}/auth/basic/login`, body, { headers: headers })
          .pipe(
            map((apiResponse: any) => {
              if (!apiResponse?.token?.access_token) {
                throw new Error(
                  "Authentication anomaly (user or user token not returned)",
                );
              }
              return authActions.loginSucceeded({
                auth: apiResponse,
                returnUrl: action.returnUrl,
              });
            }),
            catchError((error: { message: string }) =>
              of(
                authActions.loginFailed({
                  error: error,
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const signup = createEffect(
  (actions$ = inject(Actions), httpClient = inject(HttpClient)) => {
    return actions$.pipe(
      ofType(authActions.signupStarted),
      switchMap((action) => {
        const body = new HttpParams().appendAll({
          grant_type: "password",
          email: action.email,
          username: action.username,
          password: action.password,
        });
        const headers = new HttpHeaders({
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: "Basic " + btoa("binst-site" + ":" + "secret"),
        });
        return httpClient
          .post(`${settings.apiUrl}/auth/basic/register`, body, { headers: headers })
          .pipe(
            map((apiResponse: any) => {
              if (!apiResponse?.token?.access_token) {
                throw new Error(
                  "Authentication anomaly (user or user token not returned)",
                );
              }
              return authActions.signupSucceeded({
                auth: apiResponse,
                returnUrl: action.returnUrl,
              });
            }),
            catchError((error: { message: string }) =>
              of(authActions.signupFailed({ error: error })),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const loginSuccess = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthenticationService), router = inject(Router)) => {
    return actions$.pipe(
      ofType(authActions.loginSucceeded, authActions.signupSucceeded),
      map(async (action) => {
        const url = action.returnUrl;
        authService.setAuthenticationToken(action.auth)
        if (url) {
          router.navigateByUrl(url);
        } else {
          router.navigate(["/"], { queryParams: {} });
        }
      }),
    );
  },
  { functional: true, dispatch: false },
);

function sleep(seconds: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, seconds * 1000));
}


export const fetchUser = createEffect(
    (
        actions$ = inject(Actions),
        httpClient = inject(HttpClient),
        websocketService = inject(EventsWebsocketService)
    ) => {
        return actions$.pipe(
            ofType(worldActions.binstViewStarted, authActions.fetchUser),
            switchMap((action) => {
                const headers = new HttpHeaders({
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: "Basic " + btoa("binst-site" + ":" + "secret"),
                });
                // const userId = action.userId
                return httpClient
                    .get(`${settings.apiUrl}/users/me`, { headers: headers })
                    .pipe(
                        map((apiResponse: any) => {
                            return authActions.fetchUserSucceeded({
                                user: apiResponse,
                            });
                        }),
                        catchError((error: { message: string }) =>
                            of(authActions.fetchUserFailed({ error: error })),
                        ),
                    );
            }),
        );
    },
    { functional: true },
);

export const setupUserAvatarAndCompleteAuth = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        notificationPushService = inject(PushNotificationService),
        httpClient = inject(HttpClient),
        websocketService = inject(EventsWebsocketService)
    ) => {
        return actions$.pipe(
            ofType(authActions.fetchUserSucceeded),
            map(async (action) => {
                const startTarget = JSON.parse(action.user.last_coord)
                // store.dispatch(authActions.authReady({
                //     user: null,
                //     isGuest: true
                // }))
                store.dispatch(authActions.authReady({
                    user: action.user,
                    isGuest: false
                }))
                store.dispatch(cameraActions.targetMoved({
                    target: startTarget
                }))
                store.dispatch(cameraActions.zoomChanged({
                    absoluteZoom: 400
                }))
            }),
        );
    },
    { functional: true, dispatch: false },
);

export const attemptUnauthenticatedNavigation = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(authActions.attemptUnauthenticatedNavigation),
      map((action) => {
        const url = action.route;
        router.navigate(["/auth/enter"], {
          queryParams: {
            returnUrl: url,
          },
        });
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const logoutUser = createEffect(
    (
        actions$ = inject(Actions),
        authService = inject(AuthenticationService),
         router = inject(Router),
        store = inject(Store)
    ) => {
        return actions$.pipe(
            ofType(authActions.logoutUser),
            map((action) => {
                debugger;
                authService.logout()
                router.navigate([
                    {
                        outlets: {
                            primary: ['auth', 'enter'],
                            'right-pane': null,
                            'left-pane': null,
                            'new-modal-pane': null,
                            'pop-up-top-right': null
                        }
                    }
                ]);
                // router.navigate(["/auth/enter"], {
                //     queryParams: {
                //         returnUrl: null,
                //     },
                // });
            }),
        );
    },
    { functional: true, dispatch: false },
);
